import React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import ChartLegend from '../../../components/charts/ChartLegend';

type Dataset = {
  name: string;
  values: number[];
  color: string | string[];
};

type ChartData = {
  labels: string[];
  datasets: Dataset[];
  xAxis?: string;
  yAxis?: string;
};

type Props = {
  title: string;
  chartData: ChartData;
};

const TaraLineChart = ({ title, chartData }: Props) => {
  const valueFormatter = (value: number | null) => `${value ?? 0}`;
  const labels = Array.isArray(chartData.labels) ? chartData.labels : [];
  const datasets = Array.isArray(chartData.datasets) ? chartData.datasets : [];

  // build data rows
  const data = labels.map((label, idx) => {
    const row: Record<string, string | number> = { x_axis: label };
    datasets.forEach((ds) => {
      row[ds.name] = typeof ds.values[idx] === 'number' ? ds.values[idx] : 0;
    });
    return row;
  });

  // build series config
  const series = datasets.map((ds) => {
    const color = Array.isArray(ds.color)
      ? ds.color[0] || '#8884d8'
      : ds.color || '#8884d8';
    return {
      dataKey: ds.name,
      valueFormatter,
      color,
    };
  });

  return (
    <div className='pieChartComponent'>
      <h2 className='pieChartComponent__title'>{title || 'Line Chart'}</h2>
      <div className='pieChartComponent__chart'>
        <div className='pieChartComponent__chart--container'>
          <div className='pieChartComponent__chart--container-chart'>
            {data.length > 0 && series.length > 0 ? (
              <LineChart
                dataset={data}
                xAxis={[
                  {
                    scaleType: 'band',
                    dataKey: 'x_axis',
                    label: chartData.xAxis || '',
                  },
                ]}
                yAxis={[
                  {
                    label: chartData.yAxis || '',
                    min: 0,
                  },
                ]}
                series={series}
                height={315}
                margin={{ left: 70, right: 30, top: 30, bottom: 40 }}
                grid={{ vertical: true, horizontal: true }}
                sx={{
                  [`.${axisClasses.root}`]: {
                    [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                      stroke: '#8b909a',
                      strokeWidth: 3,
                    },
                    [`.${axisClasses.tickLabel}`]: {
                      fill: '#8b909a',
                    },
                  },
                  [`.${axisClasses.left} .${axisClasses.label}`]: {
                    fill: '#8b909a',
                    transform: 'translate(-20px, 0)',
                  },
                  [`.${axisClasses.bottom} .${axisClasses.label}, .${axisClasses.bottom} .${axisClasses.tickLabel}`]:
                    {
                      fill: '#8b909a',
                    },
                }}
              />
            ) : (
              <p style={{ color: 'gray', fontSize: '0.9rem' }}>
                No data available to display.
              </p>
            )}
          </div>

          {/* Single-series: flat list */}
          {datasets.length === 1 && (
            <div className='pieChartComponent__chart--container-legend'>
              <div className='pieChartComponent__chart--container-legend-content'>
                {datasets[0].values.map((val, idx) => {
                  const color = Array.isArray(datasets[0].color)
                    ? datasets[0].color[0] || '#8884d8'
                    : datasets[0].color || '#8884d8';
                  const date = labels[idx] || '';
                  return (
                    <ChartLegend
                      key={`single-${idx}`}
                      color={color}
                      label={date}
                      value={val}
                      symbol=''
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Multi-series: grouped by series */}
          {datasets.length > 1 && (
            <div className='pieChartComponent__chart--container-legendGroup'>
              {datasets.map((ds) => {
                const seriesColor = Array.isArray(ds.color)
                  ? ds.color[0] || '#8884d8'
                  : ds.color || '#8884d8';
                return (
                  <div key={ds.name}>
                    <h4 className='pieChartComponent__chart--container-legendGroup-title'>
                      {ds.name}
                    </h4>
                    <div className='pieChartComponent__chart--container-legendGroup-content'>
                      {ds.values.map((val, idx) => {
                        const date = labels[idx] || '';
                        return (
                          <ChartLegend
                            key={`${ds.name}-${idx}`}
                            color={seriesColor}
                            label={date}
                            value={val}
                            symbol=''
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}{' '}
            </div>
          )}
          {/* ----------------------- */}
        </div>
      </div>
    </div>
  );
};

export default TaraLineChart;
